import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Brain, CheckCircle, XCircle, ArrowRight, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'Engineering', name: 'Engineering', color: 'bg-blue-500', subjects: ['Computer Science', 'Electronics', 'Electrical', 'General'] },
  { id: 'JEE', name: 'JEE', color: 'bg-orange-500', subjects: ['Physics', 'Chemistry', 'Mathematics'] },
  { id: 'NEET', name: 'NEET', color: 'bg-green-500', subjects: ['Physics', 'Chemistry', 'Biology'] },
];

// Sample questions for each category and subject
const SAMPLE_QUESTIONS = {
  Engineering: {
    'Computer Science': [
      { question: "What is the time complexity of binary search?", options: ["O(n)", "O(log n)", "O(n²)", "O(1)"], correct_answer: 1 },
      { question: "Which data structure uses LIFO principle?", options: ["Queue", "Stack", "Array", "Linked List"], correct_answer: 1 },
      { question: "What does SQL stand for?", options: ["Structured Query Language", "Simple Query Language", "Standard Query Logic", "Sequential Query Language"], correct_answer: 0 },
    ],
    'Electronics': [
      { question: "What is the primary function of a transistor?", options: ["Store data", "Amplify signals", "Generate power", "Filter noise"], correct_answer: 1 },
      { question: "What type of feedback is used in oscillators?", options: ["Negative", "Positive", "Zero", "Infinite"], correct_answer: 1 },
    ],
    'Electrical': [
      { question: "What is Ohm's Law?", options: ["V = IR", "V = I/R", "V = I + R", "V = I - R"], correct_answer: 0 },
      { question: "Unit of electrical resistance is?", options: ["Volt", "Ampere", "Ohm", "Watt"], correct_answer: 2 },
    ],
    'General': [
      { question: "What does CPU stand for?", options: ["Central Program Unit", "Central Processing Unit", "Computer Personal Unit", "Central Peripheral Unit"], correct_answer: 1 },
    ]
  },
  JEE: {
    'Physics': [
      { question: "The SI unit of force is?", options: ["Joule", "Newton", "Watt", "Pascal"], correct_answer: 1 },
      { question: "Which law states F = ma?", options: ["Newton's First Law", "Newton's Second Law", "Newton's Third Law", "Kepler's Law"], correct_answer: 1 },
      { question: "The speed of light in vacuum is approximately?", options: ["3×10⁶ m/s", "3×10⁷ m/s", "3×10⁸ m/s", "3×10⁹ m/s"], correct_answer: 2 },
    ],
    'Chemistry': [
      { question: "What is the atomic number of Carbon?", options: ["4", "6", "8", "12"], correct_answer: 1 },
      { question: "Which gas is most abundant in Earth's atmosphere?", options: ["Oxygen", "Carbon Dioxide", "Nitrogen", "Hydrogen"], correct_answer: 2 },
      { question: "pH of pure water at 25°C is?", options: ["0", "7", "14", "1"], correct_answer: 1 },
    ],
    'Mathematics': [
      { question: "What is the derivative of x²?", options: ["x", "2x", "x²", "2x²"], correct_answer: 1 },
      { question: "Value of sin(90°) is?", options: ["0", "1", "-1", "1/2"], correct_answer: 1 },
      { question: "The integral of 1/x is?", options: ["x", "ln|x|", "1/x²", "eˣ"], correct_answer: 1 },
    ]
  },
  NEET: {
    'Physics': [
      { question: "The SI unit of electric current is?", options: ["Volt", "Ohm", "Ampere", "Coulomb"], correct_answer: 2 },
      { question: "Which mirror is used by dentists?", options: ["Plane", "Convex", "Concave", "Cylindrical"], correct_answer: 2 },
    ],
    'Chemistry': [
      { question: "Which vitamin is also known as Ascorbic acid?", options: ["Vitamin A", "Vitamin B", "Vitamin C", "Vitamin D"], correct_answer: 2 },
      { question: "The noble gas with highest atomic number is?", options: ["Helium", "Neon", "Argon", "Radon"], correct_answer: 3 },
    ],
    'Biology': [
      { question: "Which organelle is called the powerhouse of the cell?", options: ["Nucleus", "Ribosome", "Mitochondria", "Golgi Body"], correct_answer: 2 },
      { question: "DNA stands for?", options: ["Deoxyribonucleic Acid", "Diribonucleic Acid", "Deoxyriboneutral Acid", "None"], correct_answer: 0 },
      { question: "The largest organ in human body is?", options: ["Heart", "Liver", "Skin", "Brain"], correct_answer: 2 },
    ]
  }
};

export default function QuizPreview() {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);

  const selectCategory = (category) => {
    setSelectedCategory(category);
    setSelectedSubject(null);
    setQuizStarted(false);
  };

  const selectSubject = (subject) => {
    setSelectedSubject(subject);
  };

  const startQuiz = () => {
    const categoryQuestions = SAMPLE_QUESTIONS[selectedCategory.id]?.[selectedSubject] || [];
    setQuestions(categoryQuestions);
    setQuizStarted(true);
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setQuizCompleted(false);
  };

  const handleAnswerSelect = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;
    setShowResult(true);
    if (selectedAnswer === questions[currentQuestion].correct_answer) {
      setScore(prev => prev + 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setQuizCompleted(true);
    }
  };

  const resetQuiz = () => {
    setSelectedCategory(null);
    setSelectedSubject(null);
    setQuizStarted(false);
    setQuestions([]);
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setQuizCompleted(false);
  };

  // Category Selection View
  if (!selectedCategory) {
    return (
      <section className="py-20 bg-gradient-to-br from-brand-blue/5 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-brand-blue/10 text-brand-blue px-4 py-2 rounded-full mb-4">
              <Brain className="h-5 w-5" />
              <span className="font-medium">Quick Knowledge Check</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4">
              Test Your Skills
            </h2>
            <p className="text-lg text-slate-600">
              Choose a category to start a sample quiz!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => selectCategory(cat)}
                className="p-8 rounded-2xl border-2 border-slate-200 bg-white hover:border-brand-blue hover:shadow-lg transition-all text-left group"
              >
                <div className={`w-12 h-12 ${cat.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Brain className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-brand-dark mb-2">{cat.name}</h3>
                <p className="text-slate-600 text-sm">
                  {cat.subjects.join(', ')}
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Subject Selection View
  if (!selectedSubject) {
    return (
      <section className="py-20 bg-gradient-to-br from-brand-blue/5 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <button onClick={() => setSelectedCategory(null)} className="text-brand-blue hover:underline mb-4 inline-block">
              ← Back to Categories
            </button>
            <h2 className="text-3xl font-bold text-brand-dark mb-2">
              {selectedCategory.name} Quiz
            </h2>
            <p className="text-slate-600">Select a subject</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {selectedCategory.subjects.map((subject) => (
              <button
                key={subject}
                onClick={() => selectSubject(subject)}
                className="p-6 rounded-xl border-2 border-slate-200 bg-white hover:border-brand-blue hover:shadow-md transition-all"
              >
                <h4 className="font-semibold text-brand-dark">{subject}</h4>
              </button>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Start Quiz View
  if (!quizStarted) {
    const questionCount = SAMPLE_QUESTIONS[selectedCategory.id]?.[selectedSubject]?.length || 0;
    
    return (
      <section className="py-20 bg-gradient-to-br from-brand-blue/5 to-white">
        <div className="max-w-md mx-auto px-6 text-center">
          <button onClick={() => setSelectedSubject(null)} className="text-brand-blue hover:underline mb-4 inline-block">
            ← Back to Subjects
          </button>
          <div className={`w-20 h-20 ${selectedCategory.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
            <Brain className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-brand-dark mb-2">
            {selectedCategory.name} - {selectedSubject}
          </h2>
          <p className="text-slate-600 mb-6">{questionCount} Questions</p>
          <Button onClick={startQuiz} className="btn-primary">
            Start Quiz <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    );
  }

  // Quiz Completed View
  if (quizCompleted) {
    return (
      <section className="py-20 bg-gradient-to-br from-brand-blue/5 to-white">
        <div className="max-w-md mx-auto px-6 text-center">
          <div className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
            score >= questions.length * 0.7 ? 'bg-green-100' : score >= questions.length * 0.4 ? 'bg-yellow-100' : 'bg-red-100'
          }`}>
            <span className="text-4xl font-bold">
              {score >= questions.length * 0.7 ? '🎉' : score >= questions.length * 0.4 ? '💪' : '📚'}
            </span>
          </div>
          <h3 className="text-2xl font-bold text-brand-dark mb-2">
            Your Score: {score}/{questions.length}
          </h3>
          <p className="text-slate-600 mb-2">{selectedCategory.name} - {selectedSubject}</p>
          <p className="text-slate-500 mb-6">
            {score >= questions.length * 0.7 ? 'Excellent!' : score >= questions.length * 0.4 ? 'Good effort!' : 'Keep practicing!'}
          </p>
          <div className="flex flex-col gap-3">
            <Button onClick={resetQuiz} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" /> Try Another Quiz
            </Button>
            <Link to="/student/register">
              <Button className="btn-primary w-full">
                Sign Up for Full Access <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Quiz Question View
  const currentQ = questions[currentQuestion];

  return (
    <section className="py-20 bg-gradient-to-br from-brand-blue/5 to-white">
      <div className="max-w-3xl mx-auto px-6">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          {/* Progress */}
          <div className="h-2 bg-slate-100">
            <div 
              className={`h-full ${selectedCategory.color} transition-all`}
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>

          <div className="p-6 md:p-8">
            <div className="flex justify-between items-center mb-6">
              <span className="text-sm font-medium text-slate-500">
                {selectedCategory.name} - {selectedSubject} | Q{currentQuestion + 1}/{questions.length}
              </span>
              <span className="text-sm font-medium text-brand-blue bg-brand-blue/10 px-3 py-1 rounded-full">
                Score: {score}/{currentQuestion + (showResult ? 1 : 0)}
              </span>
            </div>

            <h3 className="text-xl md:text-2xl font-semibold text-brand-dark mb-6">
              {currentQ.question}
            </h3>

            <div className="space-y-3 mb-6">
              {currentQ.options.map((option, index) => {
                let optionClass = 'border-slate-200 hover:border-brand-blue hover:bg-brand-blue/5';
                
                if (showResult) {
                  if (index === currentQ.correct_answer) {
                    optionClass = 'border-green-500 bg-green-50 text-green-800';
                  } else if (index === selectedAnswer && index !== currentQ.correct_answer) {
                    optionClass = 'border-red-500 bg-red-50 text-red-800';
                  } else {
                    optionClass = 'border-slate-200 opacity-50';
                  }
                } else if (selectedAnswer === index) {
                  optionClass = 'border-brand-blue bg-brand-blue/10';
                }

                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    disabled={showResult}
                    className={`w-full p-4 text-left rounded-xl border-2 transition-all flex items-center gap-3 ${optionClass}`}
                  >
                    <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {String.fromCharCode(65 + index)}
                    </span>
                    <span className="flex-1">{option}</span>
                    {showResult && index === currentQ.correct_answer && <CheckCircle className="h-5 w-5 text-green-500" />}
                    {showResult && index === selectedAnswer && index !== currentQ.correct_answer && <XCircle className="h-5 w-5 text-red-500" />}
                  </button>
                );
              })}
            </div>

            {showResult && (
              <div className={`p-4 rounded-xl mb-6 ${selectedAnswer === currentQ.correct_answer ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <p className={selectedAnswer === currentQ.correct_answer ? 'text-green-700' : 'text-red-700'}>
                  {selectedAnswer === currentQ.correct_answer ? '🎉 Correct!' : `❌ Incorrect. Answer: ${String.fromCharCode(65 + currentQ.correct_answer)}`}
                </p>
              </div>
            )}

            <div className="flex justify-between">
              <Button onClick={resetQuiz} variant="outline">Exit Quiz</Button>
              {!showResult ? (
                <Button onClick={handleSubmitAnswer} disabled={selectedAnswer === null} className="btn-primary">
                  Submit
                </Button>
              ) : (
                <Button onClick={handleNextQuestion} className="btn-primary">
                  {currentQuestion < questions.length - 1 ? 'Next' : 'See Results'} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
